import { computeWindowDays } from '@gozoki/api-types/src/network-comparison'
import { capitalizeFirstLetter } from '@gozoki/tools'
import { DateTime } from 'luxon'
import { frenchDateOfIso } from '~/lib/iso-date'

const ZONE = 'Europe/Paris'

const RANGE_DASH = '–'

export interface DateRange {
  startDate: string
  endDate: string
}

export interface RangeDraft {
  startText: string
  endText: string
}

export type RangeCheck =
  | { range: DateRange; startError?: undefined; endError?: undefined }
  | { range: null; startError?: string; endError?: string }

export type DayRangeState = 'start' | 'end' | 'single' | 'in-range' | null

export interface CalendarDayCell {
  date: string
  day: number
  outside: boolean
}

export const parseDay = (date: string): DateTime => DateTime.fromISO(date, { zone: ZONE })

export const toIso = (date: DateTime): string => date.toISODate()!

export const addDays = (date: string, days: number): string => toIso(parseDay(date).plus({ days }))

export const rangeDays = ({ startDate, endDate }: DateRange): number =>
  computeWindowDays(startDate, endDate)

export const monthAbbr = (date: DateTime) => date.setLocale('fr').toFormat('LLL')
const shortYear = (date: DateTime) => date.toFormat('yy')

export function rangeText(
  { startDate, endDate }: DateRange,
  { dash = RANGE_DASH, year }: { dash?: string; year?: number } = {},
): string {
  const start = parseDay(startDate)
  const end = parseDay(endDate)
  const separator = ` ${dash} `
  if (start.year !== end.year) {
    return `${start.day} ${monthAbbr(start)} ${shortYear(start)}${separator}${end.day} ${monthAbbr(end)} ${shortYear(end)}`
  }
  const suffix = year !== undefined && end.year !== year ? ` ${shortYear(end)}` : ''
  if (start.hasSame(end, 'day')) return `${end.day} ${monthAbbr(end)}${suffix}`
  if (start.hasSame(end, 'month'))
    return `${start.day}${separator}${end.day} ${monthAbbr(end)}${suffix}`
  return `${start.day} ${monthAbbr(start)}${separator}${end.day} ${monthAbbr(end)}${suffix}`
}

export const yearOf = (date: string): number => parseDay(date).year

export const isFullMonth = ({ startDate, endDate }: DateRange): boolean => {
  const start = parseDay(startDate)
  return start.day === 1 && toIso(start.endOf('month')) === endDate
}

export const isIsoWeek = ({ startDate, endDate }: DateRange): boolean =>
  parseDay(startDate).weekday === 1 && addDays(startDate, 6) === endDate

export const monthYearLabel = (date: string): string =>
  capitalizeFirstLetter(parseDay(date).setLocale('fr').toFormat('LLLL yyyy'))

export const weekNumberOf = (date: string): number => parseDay(date).weekNumber

export const draftOf = ({ startDate, endDate }: DateRange): RangeDraft => ({
  startText: frenchDateOfIso(startDate),
  endText: frenchDateOfIso(endDate),
})

export function parseFrenchDate(text: string): string | null {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text.trim())
  if (!match) return null
  const date = DateTime.fromObject(
    { year: Number(match[3]), month: Number(match[2]), day: Number(match[1]) },
    { zone: ZONE },
  )
  return date.isValid ? toIso(date) : null
}

export function formatDateInput(raw: string): string {
  const parts = raw.split('/')
  const closed = parts.slice(0, -1).map((part, index) => {
    const digits = part.replace(/\D/g, '')
    return index < 2 && digits.length === 1 ? `0${digits}` : digits
  })
  const digits = [...closed, parts.at(-1)!.replace(/\D/g, '')].join('').slice(0, 8)
  const slashAfter = (length: number) =>
    digits.length > length || (digits.length === length && raw.endsWith('/'))
  let text = digits.slice(0, 2)
  if (slashAfter(2)) text += `/${digits.slice(2, 4)}`
  if (slashAfter(4)) text += `/${digits.slice(4)}`
  return text
}

const COMPLETABLE = /^\d{2}(\/(\d{2}\/?)?)?$/

const dateCompletion = (text: string, reference: string): string =>
  COMPLETABLE.test(text) ? frenchDateOfIso(reference).slice(text.length) : ''

export interface DraftCompletions {
  startSuffix: string
  endSuffix: string
}

export function draftCompletions(draft: RangeDraft, latest: string): DraftCompletions {
  const start = parseFrenchDate(draft.startText)
  const end = parseFrenchDate(draft.endText)
  return {
    startSuffix: dateCompletion(draft.startText, end ?? latest),
    endSuffix: dateCompletion(draft.endText, start ?? latest),
  }
}

export function completedDraft(draft: RangeDraft, latest: string): RangeDraft {
  const { startSuffix, endSuffix } = draftCompletions(draft, latest)
  return { startText: draft.startText + startSuffix, endText: draft.endText + endSuffix }
}

const INVALID_DATE = 'Date invalide (JJ/MM/AAAA)'
const AFTER_LATEST = 'Doit être avant aujourd’hui'
const END_BEFORE_START = 'Doit être après la date de début'

function dateError(date: string | null, latest: string): string | undefined {
  if (date === null) return INVALID_DATE
  return date > latest ? AFTER_LATEST : undefined
}

export function checkRangeDraft(draft: RangeDraft, latest: string): RangeCheck {
  const { startText, endText } = completedDraft(draft, latest)
  const start = parseFrenchDate(startText)
  const end = parseFrenchDate(endText)
  const startError = dateError(start, latest)
  const endError =
    dateError(end, latest) ??
    (start !== null && end !== null && end < start ? END_BEFORE_START : undefined)
  if (startError !== undefined || endError !== undefined) {
    return { range: null, startError, endError }
  }
  return { range: { startDate: start!, endDate: end! } }
}

export const orderedRange = (first: string, second: string): DateRange =>
  first <= second ? { startDate: first, endDate: second } : { startDate: second, endDate: first }

export function dayRangeState(date: string, range: DateRange | null): DayRangeState {
  if (range === null || date < range.startDate || date > range.endDate) return null
  if (range.startDate === range.endDate) return 'single'
  if (date === range.startDate) return 'start'
  if (date === range.endDate) return 'end'
  return 'in-range'
}

export function monthWeeks(month: string): CalendarDayCell[][] {
  const first = parseDay(month).startOf('month')
  const last = first.endOf('month').endOf('week')
  const weeks: CalendarDayCell[][] = []
  for (let day = first.startOf('week'); day <= last; day = day.plus({ days: 1 })) {
    if (day.weekday === 1) weeks.push([])
    weeks.at(-1)!.push({ date: toIso(day), day: day.day, outside: day.month !== first.month })
  }
  return weeks
}

export const shiftMonth = (month: string, months: number): string =>
  toIso(parseDay(month).startOf('month').plus({ months }))

export const startOfMonth = (date: string): string => toIso(parseDay(date).startOf('month'))
